import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { lodgingApi } from "@b2bportal/lodging-api";
import { axiosInstance } from "@hopper-b2b/api";
import { logger } from "@hopper-b2b/datadog";

// Function (not a module-level const) so tests can override VITE_ENV via
// vi.stubEnv. Any non-"development" value falls through to the API fetch path.
const isDevEnv = () => import.meta.env.VITE_ENV === "development";

const GoogleMapsApiKeyContext = createContext<string | undefined>(undefined);

interface GoogleMapsApiKeyProviderProps {
  children: ReactNode;
}

export const GoogleMapsApiKeyProvider = ({
  children,
}: GoogleMapsApiKeyProviderProps) => {
  // Outside dev, stay undefined while the API fetch is in flight so consumers
  // can suspend rather than render with the bogus env-var sentinel.
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState<string | undefined>(
    () =>
      isDevEnv()
        ? (import.meta.env.VITE_REACT_APP_GOOGLE_MAPS_API_KEY ?? "")
        : undefined
  );

  const handleFetch = useCallback(async () => {
    try {
      const res =
        await lodgingApi(axiosInstance).apiV0HotelsGoogleMapsApiKeyPost();
      const key = res.data?.key;
      if (typeof key !== "string" || key === "") {
        logger().error("Failed to fetch Google Maps API key: empty response");
        return;
      }
      setGoogleMapsApiKey(key);
    } catch (e) {
      // Log only safe fields. Axios errors carry the request config (incl.
      // Authorization headers and cookies) and the full response, which must
      // not be sent to Datadog.
      logger().error("Failed to fetch Google Maps API key", {
        message: e instanceof Error ? e.message : String(e),
        status:
          e != null && typeof e === "object" && "response" in e
            ? (e as { response?: { status?: number } }).response?.status
            : undefined,
      });
    }
  }, []);

  useEffect(() => {
    if (!isDevEnv()) {
      handleFetch();
    }
  }, [handleFetch]);

  return (
    <GoogleMapsApiKeyContext.Provider value={googleMapsApiKey}>
      {children}
    </GoogleMapsApiKeyContext.Provider>
  );
};

export const useGoogleMapsApiKey = (): string | undefined => {
  return useContext(GoogleMapsApiKeyContext);
};
